import { Button, Checkbox, Typography } from '@mui/material';
import { CompletionStatus } from '@strength/domain';
import {
  SetMetricMutationData,
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
} from '@strength/domain-workout';
import { MdAdd, MdSkipNext } from '@teamworksdev/icons';
import { cn } from '../../../../components/utils';
import { useMetrics } from '../../../../hooks/useMetrics';
import { useTabletContext } from '../context/TabletContext';
import { useWorkoutContext } from '../context/WorkoutContext';
import { TabletActualsMetricInput } from './tablet-inputs/TabletActualsMetricInput';

export const ExerciseSetRow = ({
  exercise,
  set,
  displayMetrics,
  originalSet,
  sortOrder,
}: {
  exercise: WorkoutExercise;
  set: WorkoutSet;
  displayMetrics: WorkoutExerciseMetric[];
  originalSet?: WorkoutSet;
  sortOrder: number;
}) => {
  const { getSelectedWorkoutId } = useTabletContext();
  const { selectedAthlete } = useWorkoutContext();
  const { actualMutationFns, context, handleEditSet } = useWorkoutContext();
  const { getMetric } = useMetrics();
  const isSetCompleted = set.status === 'completed';
  const isSetSkipped = set.status === 'skipped';
  const { libraryExerciseId, metrics: exerciseMetrics } = exercise;
  const selectedWorkoutId = getSelectedWorkoutId(selectedAthlete.id);

  if (!selectedWorkoutId) {
    return null;
  }

  const onChangeSetStatus = (newStatus: CompletionStatus) => {
    handleEditSet(selectedWorkoutId, set.workoutSetId, exercise, {
      ...set,
      status: newStatus,
    });
  };

  return (
    <>
      <Checkbox
        disabled={isSetSkipped}
        onChange={(e) =>
          onChangeSetStatus(e.target.checked ? 'completed' : 'planned')
        }
        disableRipple
        checked={isSetCompleted}
        icon={
          <Typography
            variant="subtitle1Semibold"
            className={cn(
              'p-0 text-xs border border-gray-300 rounded-sm h-full w-full grow flex items-center justify-center',
              isSetSkipped && 'border-gray-300 bg-gray-100',
            )}
          >
            {sortOrder}
          </Typography>
        }
        sx={{
          justifySelf: 'center',
          alignSelf: 'center',
          gridColumn: '1',
          padding: 0,
          height: '24px',
          width: '24px',
          '& .MuiSvgIcon-root': {
            fontSize: 28,
          },
        }}
      />
      {displayMetrics.map((m) => {
        const exerciseMetric = exerciseMetrics.find(
          (em) => em.metricId === m.metricId,
        );
        const setMetric = set.metrics.find(
          (sm) => sm.metricId === m.metricId,
        ) ?? {
          metricId: m.metricId,
          input: '',
          forCollection: false,
        };
        const orgMetric = getMetric(m.metricId);

        if (exerciseMetric && orgMetric) {
          const onEditSetMetric = (
            workoutSetId: string,
            value: SetMetricMutationData,
          ) => {
            return actualMutationFns.onEditSetMetric(
              context,
              selectedWorkoutId,
              workoutSetId,
              value,
            );
          };

          return (
            <div className="py-2" key={`${set.workoutSetId}-${m.metricId}`}>
              <TabletActualsMetricInput
                libraryExerciseId={libraryExerciseId}
                workoutSetId={set.workoutSetId}
                setMetric={setMetric}
                exerciseMetric={m}
                orgMetric={orgMetric}
                onEditSetMetric={onEditSetMetric}
                originalSetMetric={originalSet?.metrics.find(
                  (sm) => sm.metricId === m.metricId,
                )}
                disabled={isSetSkipped}
              />
            </div>
          );
        } else {
          return <div key={m.metricId}>&nbsp;</div>;
        }
      })}
      <Button
        disableRipple
        variant="text"
        onClick={() =>
          onChangeSetStatus(set.status !== 'skipped' ? 'skipped' : 'planned')
        }
        className="p-0 h-6 w-6 justify-self-center self-center"
      >
        {set.status !== 'skipped' ? (
          <MdSkipNext size={20} className="text-black/60" />
        ) : (
          <MdAdd size={20} className="text-black/60" />
        )}
      </Button>
    </>
  );
};
